/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useMutation, useLazyQuery, NetworkStatus } from '@apollo/client';

import { ToastContainer } from 'react-toastify';

import {
  DialogContent,
  Modal,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  AppBar,
  Tabs,
  Tab,
  Tooltip
} from '@mui/material';
import { makeStyles, withStyles } from '@mui/styles';

import DimensionSchedule from './Dimension/DimensionSchedule';
import DimensionPerformance from './Dimension/DimensionPerformance';
import DimensionAdherence from './Dimension/DimensionAdherence';
import Tagging from './Tagging/Tagging';
import Preview from './Preview/Preview';
import Custom from './Custom/CustomRC';
import SwitchCustom from '../../../../Switch/SwitchCustom';
import ModalDetail from '../../../../ModalDetail/ModalDetail';
import CustomTabs from '../../../../components/Tabs/CustomTabs';
import CustomSpinner from '../../../../components/LoadingScreens/CustomSpinner';

import colors from '../../../../../constants/colors'
import * as Q from '../../../../../constants/graphqlQueries';

import './CreateTab.css';
import UserRoles from '../../../../../constants/userRoles';

const CustomRadio = withStyles({
  root: {
    color: 'var(--primary)',
    borderColor: 'var(--primary)',
    borderRadius: '50%',
    padding: '0',
    boxSizing: 'border-box',
    '&$checked': {
      backgroundColor: '#0495ff',
      borderColor: colors.white,
      color: colors.white
    },
    '&:not($checked):hover': {
      color: 'var(--primary)',
      backgroundColor: '#b5daff'
    },
    transition: 'background-color 250ms'
  },
  checked: {}
})((props) => <Radio color='default' {...props} />);

const optionsSwitch = {
  leftTitle: 'Enterprise',
  rightTitle: 'Custom',
};
const CUSTOM_RULE_TYPE = 2;
const ALERT_TYPE = {
  reminder: { id: 1, label: 'Reminder', message: 'Proactive notifications for break and other scheduled activities direct to an Agent’s desktop.' },
  performance: { id: 2, label: 'Performance', message: 'Notify an Agent (and/or its associated G&A Specialist) based on the Agent’s behavior.' },
  schedule: { id: 3, label: 'Schedule', message: 'Tag and flag an Agent whenever his activity does not match his work schedule.' },
};
const SEARCH_TAB = { index: 0, label: 'Search' };
const ADHERENCE_TAB = { label: 'Adherence' };

function a11yProps(index) {
  return { id: `custom-auto-tab-${index}`, 'aria-controls': `custom-auto-tabpanel-${index}` };
};
const modalOptions = {
  title: 'Rule Type Change',
  description: 'Do you want to change rule type? The data entered will be discarded',
};

const StyledTooltip = withStyles({
  arrow: {
    color: '#F9F9F9'
  },
  tooltip: {
    backgroundColor: '#F9F9F9',
    color: colors.black,
    boxShadow: '0 0 10px rgba(0, 0, 0, 0.35)',
    padding: '1.3rem'
  }
})(Tooltip);

const modalRuleCreationOptions = { title: 'Choose rule default status: ' }

const modalRestrictOption = {
  title: 'Restricted action',
  description: 'The rule you are trying to create already exists',
}

const $ALL = 'All';
const CreateTab = ({ ruleToEdit, cleanUp, handleSelected }) => {
  const { FirstName, LastName } = useSelector((state) => state.User);
  const user = useSelector((state) => state.User);

  const [pivotKey, setPivotKey] = useState(0);
  const [alertTypeSelected, setAlertTypeSelected] = useState(ALERT_TYPE.schedule);
  const [currentPage, setCurrentPage] = useState(0);
  const [codeTypes, setCodeTypes] = useState(false);
  const [customPreview, setCustomPreview] = useState([]);
  const [dimensionPreview, setDimensionPreview] = useState([]);
  const [taggingPreview, setTaggingPreview] = useState([]);
  const [ruleIdToEdit, setRuleIdToEdit] = useState(ruleToEdit);
  const [openModal, setOpenModal] = useState(false);
  const [ruleTypeId, setRuleTypeId] = useState(1);
  const [sendDimensionData, setSendDimensionData] = useState(false);

  const [createBy, setCreateBy] = useState();
  const [createDate, setCreateDate] = useState();

  const [disabledCustomTab, setDisabledCustomTab] = useState(true);
  const [disabledDimensionTab, setDisabledDimensionTab] = useState(false);
  const [disabledTaggingTab, setDisabledTaggingTab] = useState(true);
  const [disabledPreviewTab, setDisabledPreviewTab] = useState(true);
  
  const [disabledRuleType, setDisabledRuleType] = useState(false);
  const [disabledAlertType, setDisabledAlertType] = useState(false);

  const [alarmFound, setAlarmFound] = useState('');
  const [activeRule, setActiveRule] = useState(false);
  const [openRuleStatusModal, setOpenRuleStatusModal] = useState(false);
  const [openRuleDuplicatedModal, setOpenRuleDuplicatedModal] = useState(false);
  const [ruleNameDuplicated, setRuleNameDuplicated] = useState([]);
  const [getRuleToEdit, { data: ruleData, loading: loadingRuleData }] = useLazyQuery(Q.EDIT__RULE);
  const [updateOrCreateRule, { data: newRuleCreateOrUpdate, loading: loadingResponse }] = useMutation(Q.CREATE__UPDATE__RULE);
  const [duplicatedRuleValidation, { data: validateDuplicatedRuleData, networkStatus: validateDuplicatedRuleNetworkStatus }] = useLazyQuery(Q.RULE__DUPLICATED_VALIDATION, { notifyOnNetworkStatusChange: true });

  const handleCloseRuleDuplicatedModal = () => {
    setOpenRuleDuplicatedModal(false);
  }

  const handleSubmit = async (isActive) => {
    let RuleName;
    let userNotification = {};
    let minimumThreshold;
    let maximumThreshold;
    let flagFrequency;
    let IsActive = isActive ?? activeRule;

    // codeTypes will only be true when it is a custom rule type
    if (codeTypes) RuleName = customPreview[0].ruleName;
    else RuleName = dimensionPreview[0].ruleName;

    if (alertTypeSelected.label === ALERT_TYPE.performance.label && !codeTypes && !ruleIdToEdit || 
      alertTypeSelected.label === ALERT_TYPE.reminder.label  && !codeTypes && !ruleIdToEdit) IsActive = dimensionPreview[0].isActive;
    if (alertTypeSelected.label === ALERT_TYPE.schedule.label) {
      minimumThreshold = taggingPreview[0].minimum;
      maximumThreshold = taggingPreview[0].maximum;
      flagFrequency = Number(taggingPreview[0].flagFrequency);

      userNotification = {
        notifyAgent: taggingPreview[0].notifyAgent,
        notifyAgentText: taggingPreview[0].agentNotification,
        notifyGA: taggingPreview[0].notifyGA,
        notifyGAText: taggingPreview[0].GANotification,
        ChannelId: taggingPreview[0].Channel?.AccountId || null
      }
    } else {
      // performance alert type
      minimumThreshold = dimensionPreview[0].minimum;
      maximumThreshold = dimensionPreview[0].maximum;
      flagFrequency = Number(dimensionPreview[0].flagFrequency);

      userNotification = {
        notifyAgent: dimensionPreview[0].agent_status,
        notifyAgentText: dimensionPreview[0].agent_Notification,
        notifyGA: dimensionPreview[0].GA_status,
        notifyGAText: dimensionPreview[0].GA_Notification,
        ChannelId: dimensionPreview[0].Channel?.AccountId || null
      }
    }

    await updateOrCreateRule({
      variables: {
        RuleId: ruleIdToEdit,
        RuleName,
        RuleTypeId: ruleTypeId,
        AlertTypeId: alertTypeSelected.id,
        ScheduleAlarmExternalKey: dimensionPreview[0].externalKey || null,
        ScheduleAlarmCode: dimensionPreview[0].scheduleAlarmCode || null,
        ClientCode: codeTypes ? customPreview[0].clientsID : null,
        ProgramCode: codeTypes ? customPreview[0].programsID : null,
        ProjectCode: codeTypes ? customPreview[0].projectsID : null,
        SiteCode: codeTypes ? customPreview[0].sitesID : null,
        MinThreshold: minimumThreshold,
        MaxThreshold: maximumThreshold,
        TagDuration: taggingPreview.length ? taggingPreview[0].tagDuration : null,
        TagScheduleSegmentCode: taggingPreview.length ? taggingPreview[0].tag : null,
        TagScheduleSegmentExternalKey: taggingPreview.length ? taggingPreview[0].tagKey : null,
        TaggingWindow: taggingPreview.length ? Number(taggingPreview[0].taggingWindow) : null,
        HistoricalNotes: taggingPreview.length ? taggingPreview[0].notes : '',
        FlagFrequency: flagFrequency,
        NotifyAgent: userNotification.notifyAgent,
        NotifyAgentText: userNotification.notifyAgentText,
        NotifyGA: userNotification.notifyGA,
        NotifyGAText: userNotification.notifyGAText,
        ChannelId: userNotification.ChannelId,

        IncludeACDStatus: dimensionPreview[0].agentStatus || 0,
        ACDStatus: dimensionPreview[0].ACDOption || null,
        ACDCodeMappingId: dimensionPreview[0].ACDCodeMappingId,
        ScheduleContainerCode: dimensionPreview[0].container || null,
        IncludeContainer: dimensionPreview[0].containerStatus || 0,
        ContainerCodeMappingId: dimensionPreview[0].containerId || null,
        ScheduleSegmentCode: dimensionPreview[0].segment || null,
        IncludeSegment: dimensionPreview[0].segmentStatus || 0,
        SegmentCodeMappingId: dimensionPreview[0].segmentId || null,
        AdherenceReminder: Number(dimensionPreview[0].reminder) || null,
        PerformanceRepeat: Number(dimensionPreview[0].repeat) || null,
        SourceSystemId: 2,
        CreateBy: createBy || `${FirstName} ${LastName}`,
        UpdateBy: `${FirstName} ${LastName}`,
        CreateDate: createDate || null,
        IsActive
      }
    });
  }

  const handleSubmitWithDuplicateValidation = async () => {
    await duplicatedRuleValidation({
      variables: {
        AlertTypeId: alertTypeSelected.id,
        TagDuration: taggingPreview.length ? taggingPreview[0].tagDuration : null,
        IncludeACDStatus: dimensionPreview[0].agentStatus || 0,
        ScheduleAlarmCode: dimensionPreview[0].scheduleAlarmCode || null,
        ACDStatus: dimensionPreview[0].ACDOption || null,
        IncludeContainer: dimensionPreview[0].containerStatus || 0,
        ContainerCodeMappingId: dimensionPreview[0].containerId || null,
        ScheduleSegmentCode: dimensionPreview[0].segment || null,
        IncludeSegment: dimensionPreview[0].segmentStatus || 0,
        ClientCode: codeTypes ? customPreview[0].clientsID : null,
        ProgramCode: codeTypes ? customPreview[0].programsID : null,
        ProjectCode: codeTypes ? customPreview[0].projectsID : null,
        SiteCode: codeTypes && customPreview[0].sitesID !== $ALL ? customPreview[0].sitesID : null,
        RuleTypeId: ruleTypeId,
        RuleId: ruleIdToEdit
      }
    });
  }
  const handleSubmitPageOnPreview = (active) => {
    handleSubmit(active);
    setOpenRuleStatusModal(false);
  };

  const handleOpenRuleStatusModalOverall = () => { 
    handleSubmitWithDuplicateValidation();
  }

  const useStyles = makeStyles(() => ({
    tabs: {
      '& .MuiTabs-indicator': {
        backgroundColor: colors.blue,
      },
      '& .MuiButtonBase-root.MuiTab-root': {
        fontSize: !codeTypes ? '1.8rem' : '1.4rem',
        color: colors.blue,
        fontFamily: 'Segoe UI',
        border: '0',
        background: colors.white,
        fontWeight: '600',
        textTransform: 'none',
        boxShadow: '0 0 10px 1px #d6d6d6',
      },
      '& .MuiButtonBase-root.Mui-disabled': {
        color: colors.grey,
        background: '#f2f2f2'
      },
      '& .MuiTabs-flexContainer': {
        justifyContent: 'space-between'
      }
    },
    radioTitle: {
      color: colors.blue,
      fontWeight: '500',
      fontSize: '1.8rem',
      margin: '0 0 2rem 0',
      fontFamily: 'Segoe UI',
      width: '100%',
      lineHeight: 'normal'
    },
    formControl: {
      marginLeft: '0',
      backgroundColor: colors.white,
      padding: '7px 22px 7px 6px',
      borderRadius: '2px',
      // Add conditional to dim border color for unselected radio buttons
      borderColor: disabledAlertType ? '#94c9ff' : 'var(--primary)',
      borderStyle: 'solid',
      borderWidth: 'thin',
      '&:hover': {
        backgroundColor: disabledAlertType ? colors.white : '#d5eaff'
      }
    },
    formControlSelected: {
      marginLeft: '0',
      background: 'linear-gradient(135deg, #00d5fd 0%, #0495ff 50%, #0072ee 100%)',
      padding: '7px 22px 7px 6px',
      borderRadius: '2px'
    },
    formLabel: {
      marginLeft: '1rem',
      fontSize: 'var(--caption)',
      color: colors.black,
      fontFamily: 'Segoe UI'
    },
    formLabelSelected: {
      marginLeft: '1rem',
      fontSize: 'var(--caption)',
      color: colors.white,
      fontFamily: 'Segoe UI'
    },
    formGroup: {
      alignItems: 'stretch',
      justifyContent: 'space-around',
      flexWrap: 'nowrap'
    }
  }));
  const classes = useStyles();

  const handleBack = (status, response = null) => {
    if (currentPage > 0) {
      setCurrentPage((currentPage - 1));
    };
    if (status === 'Cancel') {
      setCustomPreview([]);
      setDimensionPreview([]);
      setTaggingPreview([]);
      setCurrentPage(0);
      setRuleIdToEdit(null);
      setCodeTypes(false);
      handleSelected(SEARCH_TAB, response);
    };
    if (status === 'Clean') {
      setCustomPreview([]);
      setDimensionPreview([]);
      setTaggingPreview([]);
    };
  };
  const handleRuleType = () => {
    setCodeTypes(!codeTypes);
    setCurrentPage(0);
    setDisabledDimensionTab(!disabledDimensionTab);
    setOpenModal(false);
  };
  const handleAlertType = (value, status) => {
    const alertToFind = value === ADHERENCE_TAB.label ? 'Reminder' : value;
    const alertFinded = ALERT_TYPE[alertToFind.toLowerCase()];
    setAlertTypeSelected(alertFinded);
    if (status) handleBack('Clean');
  };
  const handleNext = () => {
    setCurrentPage((currentPage + 1));
  }
  const handleCustomData = (file) => {
    setCustomPreview(file);
    setDisabledDimensionTab(false);
    setDisabledCustomTab(true);
    handleNext();
  };
  const handleDimensionData = (file) => {
    setDimensionPreview(file);
    setDisabledTaggingTab(false);
    setDisabledDimensionTab(true);
    setAlarmFound(file[0].scheduleAlarmCode);
    handleNext();
  };
  const handleDimensionSubmit = (file) => {
    setDimensionPreview(file);
    if (!codeTypes) setSendDimensionData(true);
    else {
      setDisabledDimensionTab(true);
      setDisabledCustomTab(true);
      handleNext();
    }
  };
  const handleTaggingData = (file) => {
    setTaggingPreview(file);
    setDisabledPreviewTab(false);
    setDisabledTaggingTab(true);
    handleNext();
  };
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

useEffect(() => {
  if (validateDuplicatedRuleData && validateDuplicatedRuleNetworkStatus === NetworkStatus.ready) {
    if (validateDuplicatedRuleData.duplicatedRuleValidation) {
      if (validateDuplicatedRuleData.duplicatedRuleValidation.details.length > 0) {
        setRuleNameDuplicated(validateDuplicatedRuleData.duplicatedRuleValidation.details);
      }
      else {
        setRuleNameDuplicated([]);
      }
    }
  }
}, [validateDuplicatedRuleData, validateDuplicatedRuleNetworkStatus]);

  useEffect(() => {
    if (!ruleIdToEdit && user) {
      if (user.Role === UserRoles.WFM && user.Role !== UserRoles.Admin && user.Role !== UserRoles.Owner
        && !(user.Claims.RulesConfigurationEnterpriseCreate) && user.Claims.RulesConfigurationCustomCreate) {
        handleRuleType();
      }
    }
  }, [user]);

  useEffect(() => {
    if (ruleIdToEdit) {
      getRuleToEdit({ variables: { RuleId: ruleIdToEdit } });
    }
  }, [ruleIdToEdit]);

  useEffect(() => {
    if (newRuleCreateOrUpdate) {
      if (newRuleCreateOrUpdate.updateOrCreateRule.status === 'declined') {
        if (newRuleCreateOrUpdate.updateOrCreateRule.reason === 'duplicated') {
          setRuleNameDuplicated(newRuleCreateOrUpdate.updateOrCreateRule.details);
        }
      }
      else {
        setSendDimensionData(false);
        handleBack('Cancel', newRuleCreateOrUpdate.updateOrCreateRule.status);
      }
    }
  }, [newRuleCreateOrUpdate]);

  useEffect(() => {
    if (ruleData) {
      setDisabledAlertType(true);
      setDisabledRuleType(true);
      handleAlertType(ruleData.ruleToEdit.AlertType.Description, false);
      if (ruleData.ruleToEdit.RuleTypeId === CUSTOM_RULE_TYPE) {
        const customData = [{
          ruleName: ruleData.ruleToEdit.RuleName,
          clientsID: ruleData.ruleToEdit.Client.ClientId,
          clients: ruleData.ruleToEdit.Client.DescriptionShort,
          programsID: ruleData.ruleToEdit.Program.ProgramId,
          programs: ruleData.ruleToEdit.Program.DescriptionShort,
          projectsID: ruleData.ruleToEdit.Project.ProjectId,
          projects: ruleData.ruleToEdit.Project.DescriptionShort,
          sitesID: ruleData.ruleToEdit.Site ? ruleData.ruleToEdit.Site.SiteId : $ALL,
          sites: ruleData.ruleToEdit.Site ? ruleData.ruleToEdit.Site.DescriptionShort : $ALL
        }];
        setCustomPreview(customData);
        setCodeTypes(true)
      } else {
        setCodeTypes(false)
      }

      const ACDOption = ruleData.ruleToEdit.ACDStatus !== null ? ruleData.ruleToEdit.ACDStatus : 'Not Applicable';
      const minimum = ruleData.ruleToEdit.MinThreshold / 60;
      const maximum = ruleData.ruleToEdit.MaxThreshold / 60;
      let dimensionData;
      if (ruleData.ruleToEdit.AlertType.Description === ALERT_TYPE.schedule.label) {
        dimensionData = [{
          ruleName: ruleData.ruleToEdit.RuleName,
          scheduleAlarmCode: ruleData.ruleToEdit.ScheduleAlarmCode || '',
          externalKey: ruleData.ruleToEdit.ScheduleAlarmExternalKey || '',
          agentStatus: ruleData.ruleToEdit.IncludeACDStatus,
          containerStatus: ruleData.ruleToEdit.IncludeContainer,
          segmentStatus: ruleData.ruleToEdit.IncludeSegment,
          ACDOption,
          ACDCodeMappingId: ruleData.ruleToEdit.ACDCodeMappingId,
          containerId: ruleData.ruleToEdit.ContainerCodeMappingId || '',
          container: ruleData.ruleToEdit.ContainerType ? ruleData.ruleToEdit.ContainerType.Label : '',
          segmentId: ruleData.ruleToEdit.SegmentCodeMappingId || '',
          segment: ruleData.ruleToEdit.SegmentType ? ruleData.ruleToEdit.SegmentType.Label : ''
        }];
        const taggingData = [{
          editing: true,
          tag: ruleData.ruleToEdit.ScheduleSegmentTag ? ruleData.ruleToEdit.ScheduleSegmentTag.ScheduleSegmentCode : '',
          tagKey: ruleData.ruleToEdit.ScheduleSegmentTag ? ruleData.ruleToEdit.TagScheduleSegmentExternalKey : null,
          tagDuration: ruleData.ruleToEdit.TagDuration,
          taggingWindow: ruleData.ruleToEdit.TaggingWindow,
          notes: ruleData.ruleToEdit.HistoricalNotes,
          flagFrequency: ruleData.ruleToEdit.FlagFrequency,
          notifyAgent: ruleData.ruleToEdit.NotifyAgent,
          agentNotification: ruleData.ruleToEdit.NotifyAgentText,
          notifyGA: ruleData.ruleToEdit.NotifyGA,
          GANotification: ruleData.ruleToEdit.NotifyGAText,
          Channel: ruleData.ruleToEdit.ZoomChannel,
          minimum,
          maximum,
          scheduleAlarmCode: ruleData.ruleToEdit.ScheduleAlarmCode
        }];
        setTaggingPreview(taggingData);
      };
      if (ruleData.ruleToEdit.AlertType.Description === ALERT_TYPE.performance.label) {
        dimensionData = [{
          ruleName: ruleData.ruleToEdit.RuleName,
          ACDOption,
          ACDCodeMappingId: ruleData.ruleToEdit.ACDCodeMappingId,
          minimum: String(minimum),
          maximum: String(maximum),
          flagFrequency: ruleData.ruleToEdit.FlagFrequency,
          agent_status: ruleData.ruleToEdit.NotifyAgent,
          agent_Notification: ruleData.ruleToEdit.NotifyAgentText,
          GA_status: ruleData.ruleToEdit.NotifyGA,
          GA_Notification: ruleData.ruleToEdit.NotifyGAText,
          Channel: ruleData.ruleToEdit.ZoomChannel,
        }];
      }
      if (ruleData.ruleToEdit.AlertType.Description === ADHERENCE_TAB.label) {
        dimensionData = [{
          ruleName: ruleData.ruleToEdit.RuleName,
          containerId: ruleData.ruleToEdit.ContainerCodeMappingId,
          segmentId: ruleData.ruleToEdit.SegmentCodeMappingId,
          segment: ruleData.ruleToEdit.SegmentType ? ruleData.ruleToEdit.SegmentType.Label : '',
          reminder: String(ruleData.ruleToEdit.AdherenceReminder / 60),
          agent_status: ruleData.ruleToEdit.NotifyAgent,
          agent_Notification: ruleData.ruleToEdit.NotifyAgentText,
          GA_status: ruleData.ruleToEdit.NotifyGA,
          GA_Notification: ruleData.ruleToEdit.NotifyGAText,
          Channel: ruleData.ruleToEdit.ZoomChannel,
        }];
      }

      setDimensionPreview(dimensionData);
      setRuleTypeId(ruleData.ruleToEdit.RuleTypeId);
      setCreateBy(ruleData.ruleToEdit.CreateBy);
      if (ruleData.ruleToEdit.CreateDate) setCreateDate(ruleData.ruleToEdit.CreateDate);
      if (ruleData.ruleToEdit.IsActive === 1) setActiveRule(true);
    }
  }, [ruleData]);

  useEffect(() => {
    if (sendDimensionData) handleSubmit();
  }, [sendDimensionData]);

  useEffect(() => {
    if (codeTypes) {
      setRuleTypeId(2);
      setPivotKey(1);
    } else {
      setPivotKey(0);
      setRuleTypeId(1)
    }
  }, [codeTypes]);

  useEffect(() => {
    setDisabledAlertType(currentPage !== 0);
  }, [currentPage]);

  useEffect(() => () => cleanUp(), []);

  useEffect(() => {
    if (validateDuplicatedRuleData) {
      if (ruleNameDuplicated.length > 0) {
        setOpenRuleDuplicatedModal(true);
      }
      else if (!ruleIdToEdit) {
        setOpenRuleStatusModal(true);
      }
      else {
        handleSubmit();
      }
    }
  }, [ruleNameDuplicated]);

  return loadingRuleData ? <CustomSpinner /> : (
    <div className='__create__container'>
      <div className='create__row'>
        <div className='create__row__wrapper'>
          <p className='create__title'>Rule Type</p>
          {(() => {
            if (user) {
              let disabled = false;
              let setOption = codeTypes;

              if (user.Role === UserRoles.WFM && user.Role !== UserRoles.Admin && user.Role !== UserRoles.Owner) {
                if (!(user.Claims.RulesConfigurationEnterpriseCreate) && user.Claims.RulesConfigurationCustomCreate) {
                  disabled = true;
                  setOption = true;
                }
                if (user.Claims.RulesConfigurationEnterpriseCreate && !(user.Claims.RulesConfigurationCustomCreate)) {
                  disabled = true;
                }
              }

              return (
                <SwitchCustom
                  options={optionsSwitch}
                  handleSwitch={handleOpenModal}
                  switchStatus={setOption}
                  isDualOption
                  disabledStatus={disabled || disabledRuleType}
                />
              );
            }
            return null;
          })()}
        </div>
        <div className='create__row__wrapper'>
          <FormLabel component='legend' className={classes.radioTitle}>Alert Type</FormLabel>
          <RadioGroup row value={alertTypeSelected.label} className={classes.formGroup} onChange={(e) => handleAlertType(e.target.value, true)}>
            {Object.values(ALERT_TYPE).map(alert => (
              <StyledTooltip key={alert.id} title={alert.message}
                placement='top'
                enterDelay={400}
                leaveDelay={200}
                arrow>
                <FormControlLabel
                  classes={{
                    root: alertTypeSelected.label === alert.label ? classes.formControlSelected : classes.formControl,
                    label: alertTypeSelected.label === alert.label ? classes.formLabelSelected : classes.formLabel
                  }}
                  disabled={disabledAlertType}
                  control={<CustomRadio />}
                  value={alert.label}
                  label={alert.label}
                />
              </StyledTooltip>
            ))}
          </RadioGroup>
        </div>
      </div>
      <div className='create__wrapper'>
        <AppBar position='relative' color='default'>
          <Tabs
            value={currentPage}
            indicatorColor='primary'
            textColor='primary'
            variant='fullWidth'
            aria-label='pivot-custom'
            className={classes.tabs}
          >
            {codeTypes && <Tab disabled={disabledCustomTab} label='Custom' {...a11yProps(0)} />}
            <Tab disabled={disabledDimensionTab} label='Dimension' {...a11yProps(pivotKey)} />
            {alertTypeSelected.label === ALERT_TYPE.schedule.label && <Tab disabled={disabledTaggingTab} label='Tagging and Flagging' {...a11yProps(pivotKey + 1)} />}
            {alertTypeSelected.label === ALERT_TYPE.schedule.label || codeTypes ? <Tab disabled={disabledPreviewTab} label='Preview' {...a11yProps(pivotKey + 2)} /> : null}
          </Tabs>
        </AppBar>
        {alertTypeSelected.label === ALERT_TYPE.schedule.label &&
          /* Schedule alarm type */
          (<>
            {codeTypes && (<CustomTabs page={currentPage} index={0}>
              <Custom
                handleCustomData={handleCustomData}
                customPreview={customPreview}
                handleBack={handleBack}
              />
            </CustomTabs>)
            }
            <CustomTabs page={currentPage} index={pivotKey}>
              <DimensionSchedule
                codeTypes={codeTypes}
                handleDimensionData={handleDimensionData}
                dimensionPreview={dimensionPreview}
                taggingPreview={taggingPreview}
                alertTypeId={alertTypeSelected.id}
                handleBack={handleBack}
                ruleToEdit={ruleToEdit}
              />
            </CustomTabs>
            <CustomTabs page={currentPage} index={pivotKey + 1}>
              <Tagging
                taggingPreview={taggingPreview}
                handleTaggingData={handleTaggingData}
                handleBack={handleBack}
                alarmFound={alarmFound}
              />
            </CustomTabs>
            <CustomTabs page={currentPage} index={pivotKey + 2}>
              <Preview
                customPreview={customPreview}
                dimensionPreview={dimensionPreview}
                taggingPreview={taggingPreview}
                handleBack={handleBack}
                handleSubmit={handleOpenRuleStatusModalOverall}
                loading={loadingResponse}
                alertType={alertTypeSelected.label}
                ruleType={codeTypes}
                ruleToEdit={ruleToEdit}
              />
            </CustomTabs>
          </>)
        }
        {alertTypeSelected.label === ALERT_TYPE.performance.label &&
          /* Performance alarm type */
          (<>
            {codeTypes && (<CustomTabs page={currentPage} index={0}>
              <Custom
                handleCustomData={handleCustomData}
                customPreview={customPreview}
                handleBack={handleBack}
              />
            </CustomTabs>)
            }
            <CustomTabs page={currentPage} index={pivotKey}>
              <DimensionPerformance
                codeTypes={codeTypes}
                handleDimensionSubmit={handleDimensionSubmit}
                handleBack={handleBack}
                dimensionPreview={dimensionPreview}
                ruleToEdit={ruleToEdit}
              />
            </CustomTabs>
            {codeTypes && <CustomTabs page={currentPage} index={pivotKey + 1}>
              <Preview
                customPreview={customPreview}
                dimensionPreview={dimensionPreview}
                taggingPreview={taggingPreview}
                handleBack={handleBack}
                handleSubmit={handleOpenRuleStatusModalOverall}
                loading={loadingResponse}
                alertType={alertTypeSelected.label}
                ruleType={codeTypes}
                ruleToEdit={ruleToEdit}
              />
            </CustomTabs>}
          </>)
        }
        {alertTypeSelected.label === ALERT_TYPE.reminder.label &&
          /* Reminder alarm type */
          (<>
            {codeTypes && (<CustomTabs page={currentPage} index={0}>
              <Custom
                handleCustomData={handleCustomData}
                customPreview={customPreview}
                handleBack={handleBack}
              />
            </CustomTabs>)
            }
            <CustomTabs page={currentPage} index={pivotKey}>
              <DimensionAdherence
                codeTypes={codeTypes}
                handleDimensionSubmit={handleDimensionSubmit}
                handleBack={handleBack}
                dimensionPreview={dimensionPreview}
                ruleToEdit={ruleToEdit}
              />
            </CustomTabs>
            {codeTypes && <CustomTabs page={currentPage} index={pivotKey + 1}>
              <Preview
                customPreview={customPreview}
                dimensionPreview={dimensionPreview}
                taggingPreview={taggingPreview}
                handleBack={handleBack}
                handleSubmit={handleOpenRuleStatusModalOverall}
                loading={loadingResponse}
                alertType={alertTypeSelected.label}
                ruleType={codeTypes}
                ruleToEdit={ruleToEdit}
              />
            </CustomTabs>}
          </>)
        }

      </div>
      <Modal
        aria-labelledby='restricted-modal-title'
        aria-describedby='restricted-modal-description'
        open={openRuleDuplicatedModal}
        onClose={handleCloseRuleDuplicatedModal}
      >
        <DialogContent>
          <ModalDetail modalOptions={modalRestrictOption}>
            <div className='restricted-modal-container'>
              <ul className='restricted-modal-list'>
                {ruleNameDuplicated && ruleNameDuplicated.map(ruleName =>
                  <li key={ruleName}>
                    {ruleName}
                  </li>
                )}
              </ul>
            </div>
            <button className='simple-modal-btn' type='button' onClick={handleCloseRuleDuplicatedModal}>Ok</button>
          </ModalDetail>
        </DialogContent>
      </Modal>
      <Modal
        aria-labelledby='transition-modal-title'
        aria-describedby='transition-modal-description'
        open={openRuleStatusModal}
      >
        <DialogContent>
          <ModalDetail
            modalOptions={modalRuleCreationOptions}>
            <button className='simple-modal-btn' type='button' onClick={() => handleSubmitPageOnPreview(true)}>Active</button>
            <button className='simple-modal-btn' type='button' onClick={() => handleSubmitPageOnPreview(false)}>Paused</button>
          </ModalDetail>
        </DialogContent>
      </Modal>
      <Modal
        aria-labelledby='transition-modal-title'
        aria-describedby='transition-modal-description'
        open={openModal}
      >
        <DialogContent>
          <ModalDetail
            modalOptions={modalOptions}>
            <button className='simple-modal-btn' type='button' onClick={handleRuleType}>Confirm</button>
            <button className='simple-modal-btn' type='button' onClick={handleCloseModal}>Cancel</button>
          </ModalDetail>
        </DialogContent>
      </Modal>
      <ToastContainer hideProgressBar />
    </div>
  );
};

CreateTab.propTypes = {
  ruleToEdit: PropTypes.number,
  cleanUp: PropTypes.func,
  handleSelected: PropTypes.func.isRequired
};

CreateTab.defaultProps = {
  ruleToEdit: null,
  cleanUp: null
};

export default CreateTab;
